import type { HydratedCustomSection } from '@/features/customization/schema/custom-section.schema';
import { findComplaintAndAssessment, getCodedComplaintDisplay } from '@/features/prescription/chief-complaint/utils';
import { extractConsultationDetailsTimelineEntries } from '@/features/prescription/consultation-details/utils';
import { getCustomSectionValues } from '@/features/prescription/custom-section/utils';
import { getObsValue as getEncounterObsValue } from '@/features/prescription/past-prescription-panel/utils';
import { mapEncounterToPsychometricAssessment } from '@/features/prescription/psychometric-assessment/utils';
import { COMPLAINT_CONCEPTS, CONCEPT, CONCEPTS, EMPTY_RESULT, EXAM_MAPPING, NA_RESULT } from '@/shared/constants';
import type {
  ComplaintData,
  Diagnosis,
  Encounter,
  Option,
  Order,
  TimelineLabeledData,
  TimelineLabeledEntry,
} from '@/shared/types';
import type { VisitEncounter } from '@/shared/types/prescription/visit';
import { isJsonString, isValidValue } from '@/shared/utils/helpers';
import { extractShortDisplay } from '@/shared/utils/prescription/medication';
import { generateTipTapHTML, isValidTipTapDoc, parseStoredRichTextValue } from '@/shared/utils/tiptap';

const isDisplayableTimelineValue = (value: unknown): value is string =>
  isValidValue(value) && value !== EMPTY_RESULT && value !== NA_RESULT;

export const toRichTextTimelineEntry = (label: string, rawValue: unknown): TimelineLabeledEntry | null => {
  if (!isDisplayableTimelineValue(rawValue)) return null;
  const parsed = parseStoredRichTextValue(String(rawValue));
  if (parsed.isTipTapDoc && !parsed.plainText) return null;
  return {
    label,
    value: parsed.plainText || String(rawValue),
    isTipTapDoc: parsed.isTipTapDoc,
    html: parsed.html,
  };
};

const toLabeledTimelineJson = (entries: TimelineLabeledEntry[]): string => {
  const data: TimelineLabeledData = { entries, hasData: entries.length > 0 };
  return data.hasData ? JSON.stringify(data) : '-';
};

export const processComplaintData = (encounters: Encounter[]): string => {
  const { complaintEncounters, verbalTextEncounters } = findComplaintAndAssessment(encounters);

  const complaintData: ComplaintData = {
    freeInput: {},
    codedComplaints: null,
    hasData: false,
  };

  // Process verbal text (free input)
  const verbalTextEncounter = verbalTextEncounters?.[0];
  const verbalTextObs = verbalTextEncounter?.obs?.find((obs: any) =>
    obs?.concept?.display?.includes(COMPLAINT_CONCEPTS.VERBAL_TEXT),
  );

  if (verbalTextObs?.value) {
    const value = verbalTextObs.value;
    const isTipTapDoc = isJsonString(value) ? isValidTipTapDoc(JSON.parse(value)) : false;

    complaintData.freeInput = {
      value,
      isTipTapDoc,
      html: isTipTapDoc ? generateTipTapHTML(JSON.parse(value)) : null,
      encounter: verbalTextEncounter,
    };
    complaintData.hasData = true;
  }

  // Process coded complaints
  const codedComplaints: NonNullable<ComplaintData['codedComplaints']> = [];
  complaintEncounters.forEach(complaint => {
    const { complaintDisplay, durationText } = getCodedComplaintDisplay(complaint);
    if (complaintDisplay) {
      codedComplaints.push({
        display: `${complaintDisplay}${durationText ? ` (${durationText})` : ''}`,
        encounter: complaint,
      });
    }
  });

  if (codedComplaints.length > 0) {
    complaintData.codedComplaints = codedComplaints;
    complaintData.hasData = true;
  }

  return complaintData.hasData ? JSON.stringify(complaintData) : '-';
};

export const processDiagnosisData = (diagnoses: Diagnosis[]): string => {
  const values = diagnoses
    .map((diagnosis, index) => (diagnosis?.display ? `${index + 1}. ${diagnosis?.display}` : null))
    .filter(Boolean);

  return values.join('\n') || '-';
};

export const processDrugOrderData = (drugOrders: Order[], frequencyUnits: Option[]): string => {
  const values = drugOrders
    .map((drugOrder, index) => {
      const frequency = frequencyUnits?.find(unit => unit.label.includes(drugOrder?.frequency?.display))?.label;

      const extractedFrequency = extractShortDisplay(frequency ?? '');
      const frequencyDisplay = isValidValue(extractedFrequency) ? `(${extractedFrequency})` : '';

      const duration = isValidValue(drugOrder?.duration) ? drugOrder?.duration?.toString() : '';
      const durationUnits = isValidValue(drugOrder?.durationUnits?.display)
        ? `${drugOrder?.durationUnits?.display}`
        : '';
      const durationDisplay = duration?.length > 0 && durationUnits?.length > 0 ? `- ${duration} ${durationUnits}` : '';

      const display = `${drugOrder?.drug?.display} ${durationDisplay} ${frequencyDisplay}`;

      return display ? `${index + 1}. ${display}` : null;
    })
    .filter(Boolean);

  return values.join('\n') || '-';
};

export const processRadiologyOrInvestigationData = (orders: Order[]): string => {
  const values = orders
    .map((order, index) => {
      const display = order?.concept?.display;
      return display ? `${index + 1}. ${display}` : null;
    })
    .filter(Boolean);

  return values.join('\n') || '-';
};

export const processHopiData = (encounters: Encounter[]): string => {
  const entries: TimelineLabeledEntry[] = [];

  encounters.forEach(encounter => {
    const historyOfPresentIllness = getEncounterObsValue(encounter, CONCEPT.HOPI.HOPI);
    const negativeHistory = getEncounterObsValue(encounter, CONCEPT.HOPI.NEGATIVE);
    const hopi = toRichTextTimelineEntry('History of Present Illness', historyOfPresentIllness);
    if (hopi) entries.push(hopi);
    const negative = toRichTextTimelineEntry('Negative History', negativeHistory);
    if (negative) entries.push(negative);
  });
  return toLabeledTimelineJson(entries);
};

export const processSystemicExaminationData = (encounters: Encounter[]): string => {
  const entries: TimelineLabeledEntry[] = [];

  encounters.forEach(encounter => {
    EXAM_MAPPING.forEach(({ concept }) => {
      const value = getEncounterObsValue(encounter, concept);
      const entry = toRichTextTimelineEntry(concept, value);
      if (entry) entries.push(entry);
    });
  });

  return toLabeledTimelineJson(entries);
};

const PSYCHOLOGY_NOTE_TIMELINE_FIELDS = [
  { label: 'Session Note', concept: CONCEPTS.PSYCHOLOGY_NOTE.CHILD.SESSION_NOTE },
  { label: 'Summary for Doctor', concept: CONCEPTS.PSYCHOLOGY_NOTE.CHILD.SUMMARY_FOR_DOCTOR },
  { label: 'Advice & Plan', concept: CONCEPTS.PSYCHOLOGY_NOTE.CHILD.ADVICE_AND_PLAN },
] as const;

export const processPsychologyNoteData = (encounters: Encounter[]): string => {
  const entries: TimelineLabeledEntry[] = [];

  encounters.forEach(encounter => {
    PSYCHOLOGY_NOTE_TIMELINE_FIELDS.forEach(({ label, concept }) => {
      const entry = toRichTextTimelineEntry(label, getEncounterObsValue(encounter, concept));
      if (entry) entries.push(entry);
    });
  });

  return toLabeledTimelineJson(entries);
};

export const processConsultationDetailsData = (encounters: Encounter[]): string => {
  const entries = extractConsultationDetailsTimelineEntries(encounters)
    .map(({ label, value }) => toRichTextTimelineEntry(label, value))
    .filter((entry): entry is TimelineLabeledEntry => Boolean(entry));

  return toLabeledTimelineJson(entries);
};

export const processCustomSectionData = (encounters: Encounter[], section?: HydratedCustomSection): string => {
  if (!section) return '-';

  const entries = getCustomSectionValues(encounters as VisitEncounter[], section)
    .map(field => toRichTextTimelineEntry(field.label, field.value))
    .filter((entry): entry is TimelineLabeledEntry => Boolean(entry));

  return toLabeledTimelineJson(entries);
};

export const processGenericFieldData = (encounters: Encounter[]): string => {
  const displayData = encounters
    .map(encounter => {
      const raw = encounter.obs ? encounter?.obs?.[0]?.value : JSON.stringify(encounter);
      if (!isDisplayableTimelineValue(raw)) return null;
      const parsed = parseStoredRichTextValue(String(raw));
      if (parsed.isTipTapDoc && !parsed.plainText) return null;
      return String(raw);
    })
    .filter(Boolean)
    .join(',\n ');

  return displayData || '-';
};

export const processPsychometricAssessmentData = (encounters: Encounter[]): string => {
  const mappedPsychometricAssessments = mapEncounterToPsychometricAssessment(encounters);
  const displayData = mappedPsychometricAssessments
    .map(assessment => {
      if (assessment.score) return `${assessment?.question?.value?.display}'s score - ${assessment?.score}`;
    })
    .join(',\n ');

  return displayData || '-';
};
